import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CaretSortIcon } from '@radix-ui/react-icons';
import { Button } from '@/components/ui/button.tsx';
import { Check } from 'lucide-react';
import { cn } from '@/utils/utils.ts';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  ConditionFormFields,
  getAvailableParameters,
  getParameterLabel,
} from '@/features/rules-shared/schemas/condition-rules.utils.ts';

export function ParameterComboboxSelect({ index }: { index: number }) {
  const [open, setOpen] = useState(false);

  const form = useFormContext<ConditionFormFields>();
  const watchRuleType = form.watch('ruleType');
  const watchDeviceType = form.watch('deviceType');
  const watchConditionFact = form.watch(`conditions.${index}.fact`);

  const availableParameters = getAvailableParameters({
    watchRuleType,
    watchDeviceType,
  });

  const parameterLabel = getParameterLabel({
    watchDeviceType,
    watchRuleType,
    fieldValue: watchConditionFact,
  });

  return (
    <FormField
      control={form.control}
      name={`conditions.${index}.fact`}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="required">Parameter</FormLabel>
          <Popover open={open} onOpenChange={setOpen} modal={true}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between px-3 py-2 text-sm font-normal"
                >
                  <div className="overflow-hidden">
                    {field.value ? (
                      parameterLabel
                    ) : (
                      <span className="text-muted-foreground">Select parameter</span>
                    )}
                  </div>
                  <CaretSortIcon className="min-h-4 min-w-4 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="p-0" align="start">
              <Command>
                <CommandInput placeholder="Search parameter" className="h-9" />
                <CommandList>
                  <CommandEmpty>No parameter found.</CommandEmpty>
                  <CommandGroup>
                    {availableParameters?.map((param) => (
                      <CommandItem
                        key={param.id}
                        value={param.id}
                        onSelect={() => {
                          const newValue = param.id === field.value ? '' : String(param.id);
                          field.onChange(newValue);
                          if (watchRuleType === 'schedule' && newValue) {
                            form.setValue(`conditions.${index}.operator`, 'equal');
                            form.setValue(`conditions.${index}.value`, 'true');
                          }
                          setOpen(false);
                        }}
                      >
                        {param.name} {param.unit && `(${param.unit})`}
                        <Check
                          className={cn(
                            'ml-auto',
                            field.value === param.id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
